const CREDIT_CARD_MANAGE_MODE_ID = 'CreditCard:ManageMode';
const CREDIT_CARD_NEW_INSTRUMENT_ID = -1;

const {
    assign, each, find, filter, first, remove,
} = require('lodash');
const singleSelectTypes = require('../digital-pay-single-select-type.constants');

class CreditCardController {
    constructor($q, settings, moveFocus, digitalPayPaymentInstrumentsService, digitalPayPaymentDistributionService, digitalPayPaymentErrorDisplayService, digitalPayErrorMessageGenerator) {
        this.$q = $q;
        this.settings = settings;
        this.moveFocus = moveFocus;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
        this.digitalPayPaymentDistributionService = digitalPayPaymentDistributionService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
    }

    $onInit() {
        this.emptyCard = { PaymentInstrumentId: undefined };
        this.isAddNewCreditCard = false;
        this.isManageMode = false;
        this.selectedPaymentInstrumentId = undefined;
        this.creditCardStepUpTokens = [];
        this.deleteMessage = this.settings.digitalPayMessages.creditCardDeleteMessage;

        // Select the last paid credit card if the initial single select type (last transaction) was credit card (if it's not expired)
        if (this.singleSelectType === singleSelectTypes.CREDITCARD) {
            if (this.creditCards.length && !this.creditCards[0].Expired) {
                this.selectCard(this.creditCards[0].PaymentInstrumentId);
            } else {
                this.setSingleSelectType({ type: singleSelectTypes.NONE });
            }
        }
    }

    showActions() {
        return this.showAddCreditCard() || this.hasCreditCards();
    }

    showAddCreditCard() {
        return !this.isManageMode && !this.isAddNewCreditCard && this.creditCards.length;
    }

    hasCreditCards() {
        return this.creditCards.length;
    }

    hasValidCards() {
        return this.creditCards.length > this.getExpiredCount();
    }

    toggleAddNewCreditCard() {
        this.unselectCard(false);
        this.selectCard(CREDIT_CARD_NEW_INSTRUMENT_ID);
        this.isAddNewCreditCard = !this.isAddNewCreditCard;
        if (!this.isAddNewCreditCard) {
            this.setSingleSelectType({ type: singleSelectTypes.NONE });
        }
    }

    toggleManageMode() {
        this.cardInEditMode = this.emptyCard;
        if (this.isManageMode) {
            // Re-enable payment when exiting Manage Mode
            this.paymentStateDefinitions.removeDisablePaymentState(CREDIT_CARD_MANAGE_MODE_ID);
            this.onInstrumentTypeInManageMode({ type: this.settings.digitalPayPaymentTypes.emptyType });
            this.isManageMode = false;
        } else {
            // Disable payment if in Manage Mode
            this.enterManageMode();
            if (this.isAddNewCreditCard) {
                // Close the Add New Credit Panel if Manage Mode is turned on (fine to discard data in the panel)
                this.toggleAddNewCreditCard();
            }
        }
    }

    enterManageMode() {
        this.paymentStateDefinitions.addDisablePaymentState(CREDIT_CARD_MANAGE_MODE_ID, this.settings.digitalPayScreenReaderMessages.creditCardManageModeDisableMessage);
        this.onInstrumentTypeInManageMode({ type: this.settings.digitalPayPaymentTypes.creditCardType });
        this.isManageMode = true;
        this.isAddNewCreditCard = false;
        this.moveFocus('#digitalPayCreditCard');
    }

    getExpiredCount() {
        return filter(this.creditCards, card => card.Expired).length;
    }

    getExpiredErrorMessage() {
        return this.getExpiredCount() > 1
            ? this.settings.digitalPayError.creditCardMultipleExpiredWarning
            : this.settings.digitalPayError.creditCardSingleExpiredWarning;
    }

    toggleSelectCreditCard(card) {
        if (this.isManageMode) {
            return;
        }

        if (card.PaymentInstrumentId === this.selectedPaymentInstrumentId) {
            // Toggle off
            this.unselectCard();
        } else {
            // Toggle on
            this.unselectCard(false);
            this.selectCard(card.PaymentInstrumentId);
        }
    }

    removeCreditCard(card) {
        // Confirmation Dialog is defined in the html
        this.confirmationDialog.show().then((dialogResponse) => {
            if (!dialogResponse.success) {
                return;
            }

            this.digitalPayPaymentInstrumentsService.deleteDigitalPayInstrument({ paymentInstrumentId: card.PaymentInstrumentId }).then((response) => {
                if (response.Success) {
                    // Ensure that the card to be removed is not selected
                    if (card.PaymentInstrumentId === this.selectedPaymentInstrumentId) {
                        this.unselectCard();
                    }
                    remove(this.creditCards, cardToDelete => cardToDelete.PaymentInstrumentId === card.PaymentInstrumentId);
                    // When there are no non expired cards
                    if (!this.hasCreditCards()) {
                        this.toggleManageMode();
                    }
                    this.refreshCreditCards();
                } else if (response.ErrorCode === this.settings.digitalPayErrorCodes.creditCardDeleteLinkedCardForbidden) {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError(this.settings.digitalPayError.creditCardDeleteLinkedCardForbiddenMessage);
                } else {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError(this.settings.digitalPayError.creditCardDeleteErrorMessage);
                }
            }).catch(() => {
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(this.settings.digitalPayError.creditCardDeleteErrorMessage);
            });
        });
    }

    selectCard(paymentInstrumentId) {
        this.selectedPaymentInstrumentId = paymentInstrumentId;
        this.setSingleSelectType({ type: singleSelectTypes.CREDITCARD });

        // Deselect add new credit card if a different credit card gets selected
        if (paymentInstrumentId !== CREDIT_CARD_NEW_INSTRUMENT_ID) {
            this.isAddNewCreditCard = false;
        }
    }

    unselectCard(resetSingleSelectType = true) {
        if (!this.selectedPaymentInstrumentId) {
            return;
        }
        this.digitalPayPaymentDistributionService.removePayment(this.selectedPaymentInstrumentId);
        this.prePaymentActionDefinitions.removeAction(this.selectedPaymentInstrumentId);
        if (resetSingleSelectType) {
            // Suppress when switching to a different credit card for efficiency
            this.setSingleSelectType({ type: singleSelectTypes.NONE });
        }
        this.selectedPaymentInstrumentId = undefined;
    }

    confirmDialog() {
        this.confirmationDialog.close({ success: true });
    }

    notifyAddCardError(errorMessage) {
        this.addCardError = errorMessage;
    }

    notifyUpdate(paymentInstrumentId, stepUpToken) {
        this.creditCardStepUpTokens.push({ paymentInstrumentId, stepUpToken });
        this.refreshCreditCards();
    }

    notifyEditCard(card) {
        if (!this.isManageMode) {
            this.enterManageMode();
        }
        this.cardInEditMode = card || this.emptyCard;
    }

    notifyErroredCard(id) {
        this.onErroredCard(id);
    }

    onErroredCard(id) {
        this.erroredSavedCardId = id;
        this.unselectCard();
    }

    assignStepUpTokens() {
        if (!this.creditCardStepUpTokens || !this.creditCardStepUpTokens.length) {
            return;
        }

        each(this.creditCardStepUpTokens, (instrumentTokenPair) => {
            const matchingCard = find(this.creditCards, card => card.PaymentInstrumentId === instrumentTokenPair.paymentInstrumentId);
            if (matchingCard) {
                assign(matchingCard, { stepUpToken: instrumentTokenPair.stepUpToken });
            }
        });
        this.creditCardStepUpTokens = [];
    }

    $onChanges(changes) {
        if (changes.singleSelectType && changes.singleSelectType.previousValue === singleSelectTypes.CREDITCARD && changes.singleSelectType.currentValue !== singleSelectTypes.CREDITCARD) {
            // Another single select type payment method has been selected hence unselect all
            if (this.isAddNewCreditCard) {
                this.isAddNewCreditCard = false;
                return;
            }
            // Don't set single select type - the other payment method already does this
            this.unselectCard(false);
        }

        const currentPaymentErrorStates = changes.paymentErrorStates && changes.paymentErrorStates.currentValue || [];
        if (currentPaymentErrorStates.length) {
            // Filter all credit card errors.
            const creditCardErrorState = first(filter(currentPaymentErrorStates, (value) => {
                const state = value.state;
                return state.ErrorCode && state.PaymentInstrumentType === this.settings.digitalPayPaymentTypes.creditCardType;
            }));

            // Clear existing error states.
            this.erroredSavedCardId = undefined;
            if (creditCardErrorState) {
                if (!this.isAddNewCreditCard && !creditCardErrorState.state.StepUp) {
                    const creditCard = find(this.creditCards, c => c.PaymentInstrumentId === creditCardErrorState.state.PaymentInstrumentId);
                    if (creditCard && creditCardErrorState.state.ErrorCode === this.settings.digitalPayOrderPlacementErrorCodes.declined) {
                        this.onErroredCard(creditCardErrorState.state.PaymentInstrumentId);
                    } else if (creditCard) {
                        this.erroredSavedCardId = creditCard.PaymentInstrumentId;
                        const suffix = creditCard.CardSuffix;
                        const isSplitPayment = this.digitalPayErrorMessageGenerator.getPaymentErrorMode();
                        const messageTemplate = isSplitPayment
                            ? this.settings.digitalPayError.creditCardSavedCardSplitPaymentFailure
                            : this.settings.digitalPayError.creditCardSavedCardPaymentFailure;
                        const errorMessage = this.digitalPayErrorMessageGenerator.formatErrorMessage(messageTemplate, [suffix]);
                        this.digitalPayPaymentErrorDisplayService.pushToPaymentErrorBreakdown(errorMessage);
                        this.unselectCard();
                    }
                }
            } else {
                // clean up just for readability.
                this.erroredSavedCardId = undefined;
            }
        } else if (changes.paymentErrorStates) {
            // Clear existing error states.
            this.erroredSavedCardId = undefined;
        }
        if (changes.creditCards) {
            this.assignStepUpTokens();
        }
    }
}

CreditCardController.$inject = ['$q', 'Settings', 'MoveFocus', 'DigitalPayPaymentInstrumentsService', 'DigitalPayPaymentDistributionService', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayErrorMessageGenerator'];

const creditCardComponentDefinition = {
    controller: CreditCardController,
    template: require('./credit-card.component.html').trim(),
    bindings: {
        allowedNetworks: '<',
        creditCards: '<',
        hasPrePaymentActionFailures: '<',
        isImpersonated: '<',
        isSavingCard: '<',
        onInstrumentTypeInManageMode: '&',
        paymentErrorStates: '<',
        paymentStateDefinitions: '<',
        prePaymentActionDefinitions: '<',
        refreshCreditCards: '&?', // Used in the view
        setSingleSelectType: '&',
        shouldRemoveNewlyAddedInstruments: '<',
        shouldShowPayingAmount: '<',
        shouldShowSave: '<',
        singleSelectType: '<', // Watch using $onChanges
    },
};

module.exports = {
    CreditCardController,
    creditCardComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/creditcard/credit-card.component.js